import {
  Badge,
  Button,
  Card,
  Group,
  List,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Sparkles } from 'lucide-react';
import type {
  AnalysisVerificationResult,
  SpectralAnalysisSnapshot,
  VerificationStatus,
} from '../types';
import {
  buildAllChecksPassedMessage,
  buildDeterministicChecksHeadline,
} from './formatVerificationCopy';
import {
  getOpenAiVerificationErrorMessage,
  useSpectralVerification,
} from './useSpectralVerification';

const statusLabel: Record<VerificationStatus, string> = {
  'not-run': 'Non eseguita',
  verified: 'Verificata',
  warning: 'Attenzione',
  failed: 'Fallita',
};

const statusColor: Record<VerificationStatus, string> = {
  'not-run': 'gray',
  verified: 'teal',
  warning: 'yellow',
  failed: 'red',
};

const formatDate = (value?: string) =>
  value ? new Date(value).toLocaleString('it-IT') : 'n/a';

interface PipelineVerificationCardProps {
  snapshot: SpectralAnalysisSnapshot;
}

export function PipelineVerificationCard({
  snapshot,
}: PipelineVerificationCardProps) {
  const { verification, isVerifying, runVerification, isConfigured } =
    useSpectralVerification(snapshot.verification);

  const handleVerify = async () => {
    try {
      const result = await runVerification(snapshot);
      notifications.show({
        color: result.status === 'verified' ? 'teal' : 'yellow',
        title: 'Verifica pipeline completata',
        message:
          result.notes[0] ??
          `Stato: ${statusLabel[result.status] ?? result.status}`,
      });
    } catch (error) {
      notifications.show({
        color: 'red',
        title: 'Verifica pipeline fallita',
        message: getOpenAiVerificationErrorMessage(
          error instanceof Error ? error : new Error('Errore sconosciuto')
        ),
      });
    }
  };

  return (
    <Card withBorder>
      <Group justify='space-between' align='flex-start' mb='sm'>
        <div>
          <Title order={4}>Verifica pipeline</Title>
          <Text size='sm' c='dimmed'>
            Controlli numerici automatici e revisione metodologica locale sulla
            coerenza della normalizzazione spettrale.
          </Text>
        </div>
        <Badge color={statusColor[verification.status]} variant='light'>
          {statusLabel[verification.status]}
        </Badge>
      </Group>

      {!isConfigured && (
        <Text size='sm' c='dimmed' mb='sm'>
          Verifica non disponibile.
        </Text>
      )}

      <Group mb='md'>
        <Button
          leftSection={<Sparkles size={16} />}
          loading={isVerifying}
          disabled={!isConfigured || snapshot.includedSessions.length === 0}
          onClick={() => void handleVerify()}
        >
          {isVerifying ? 'Verifica in corso' : 'Esegui verifica'}
        </Button>
        {verification.checkedAt && (
          <Text size='xs' c='dimmed'>
            Ultima verifica: {formatDate(verification.checkedAt)}
          </Text>
        )}
      </Group>

      {verification.status !== 'not-run' && (
        <Stack gap='sm'>
          {verification.notes.length > 0 && (
            <div>
              <Text size='sm' fw={600} mb={4}>
                Esito
              </Text>
              <Text size='sm'>{verification.notes[0]}</Text>
              {verification.notes.length > 1 && (
                <List size='sm' spacing={4} mt='xs'>
                  {verification.notes.slice(1).map(note => (
                    <List.Item key={note} c='red'>
                      {note}
                    </List.Item>
                  ))}
                </List>
              )}
            </div>
          )}

          {verification.deterministicChecks &&
            verification.deterministicChecks.length > 0 && (
              <div>
                <Text size='sm' fw={600} mb={4}>
                  {buildDeterministicChecksHeadline(
                    verification.deterministicChecks.filter(
                      check => check.passed
                    ).length,
                    verification.deterministicChecks.length
                  )}
                </Text>
                {verification.deterministicChecks.every(
                  check => check.passed
                ) ? (
                  <Text size='sm' c='teal'>
                    {buildAllChecksPassedMessage()}
                  </Text>
                ) : (
                  <List size='sm' spacing={4}>
                    {verification.deterministicChecks
                      .filter(check => !check.passed)
                      .slice(0, 8)
                      .map(check => (
                        <List.Item key={check.id} c='red'>
                          {check.message}
                        </List.Item>
                      ))}
                  </List>
                )}
              </div>
            )}

          {verification.aiFindings && verification.aiFindings.length > 0 && (
            <div>
              <Text size='sm' fw={600} mb={4}>
                Dettaglio per area
              </Text>
              <List size='sm' spacing={4}>
                {verification.aiFindings.map(finding => (
                  <List.Item key={finding}>{finding}</List.Item>
                ))}
              </List>
            </div>
          )}
        </Stack>
      )}
    </Card>
  );
}

export const defaultVerificationResult = (): AnalysisVerificationResult => ({
  status: 'not-run',
  notes: ['Verifica non ancora eseguita'],
});
